/**
 * Avatar URL helper for consistent cache busting
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/**
 * Constructs a full avatar URL from avatar_path
 */
function constructAvatarUrlFromPath(avatarPath: string, version?: number): string {
  const baseUrl = `${SUPABASE_URL}/storage/v1/object/public/avatars/${avatarPath}`;
  return `${baseUrl}?v=${version || 0}`;
}

/**
 * Returns avatar URL with cache-busting version parameter
 * Supports both avatar_url (legacy) and avatar_path (preferred)
 *
 * Priority: avatar_url > avatar_path
 */
export function getAvatarSrc(
  avatarUrl: string | null | undefined,
  version?: number | null,
  avatarPath?: string | null
): string | undefined {
  // If avatar_url is set, use it with cache busting
  if (avatarUrl) {
    try {
      const urlObj = new URL(avatarUrl);
      urlObj.searchParams.set('v', String(version || 0));
      return urlObj.toString();
    } catch {
      // Fallback for relative URLs or malformed URLs
      const separator = avatarUrl.includes('?') ? '&' : '?';
      return `${avatarUrl}${separator}v=${version || 0}`;
    }
  }

  // If no avatar_url but avatar_path is set, construct URL from path
  if (avatarPath) {
    return constructAvatarUrlFromPath(avatarPath, version || 0);
  }

  return undefined;
}

/**
 * Helper to get avatar src from a profile object
 * This is the preferred way to get avatar URL from creator_profiles data
 */
export function getAvatarSrcFromProfile(profile: {
  avatar_url?: string | null;
  avatar_path?: string | null;
  avatar_version?: number | null;
} | null | undefined): string | undefined {
  if (!profile) return undefined;
  return getAvatarSrc(profile.avatar_url, profile.avatar_version, profile.avatar_path);
}
